import { getEnumFlags, MicroTimer, toEnumFlags } from '@avantia/client-and-server-utilities';
import { createIsolatedPackageLoggerWithFlags, createLoggerV2 } from '@avantia/logging-and-errors';

export enum HpClientDebugFlags {
  None = 0,
  Authentication = 2 << 0,
  Configuration = 2 << 1,
  RestApi = 2 << 2,
  TimeTaken = 2 << 3,
  UnitTests = 2 << 4,
  ReducerProcessor = 2 << 5,
  All = Authentication | Configuration | RestApi | TimeTaken | UnitTests | ReducerProcessor
}

const v1Log = createIsolatedPackageLoggerWithFlags<HpClientDebugFlags>(
  'HpClientDebugFlags',
  HpClientDebugFlags.None,
  HpClientDebugFlags.All
);

// The chainable, DataDog-friendly logger - the only logger app code should call directly. Its
// colourise/level settings are read live via setLoggerV2Settings, so creation order relative to
// that call doesn't matter.
export const log = createLoggerV2();

/**
 * Checks whether a HpClientDebugFlags value is currently active, for gating log events via
 * `.enabledIf(hasFlag(HpClientDebugFlags.X))`.
 */
export function hasFlag(flag: HpClientDebugFlags): boolean {
  return (v1Log.getFlags() & flag) === flag;
}

const {
  notice: logNotice,
  critical: logCritical,
  setLogger: setLoggerForClient,
  getLogger: getLoggerForClient,
  setFlags: setLoggerFlagsForClient,
  getFlags: getLoggerFlagsForClient
} = v1Log;

export {
  logNotice,
  logCritical,
  setLoggerForClient,
  getLoggerForClient,
  setLoggerFlagsForClient,
  getLoggerFlagsForClient
};

export function displayTimeTaken(description: string, timer: MicroTimer): void {
  log
    .debug()
    .enabledIf(hasFlag(HpClientDebugFlags.TimeTaken))
    .str('description', description)
    .dur('elapsedMs', timer.elapsedTime())
    .msg('Time taken');
}

export function recordElapsedTime<ResultT>(description: string, action: () => ResultT): ResultT {
  const timer = new MicroTimer();
  const res = action();
  displayTimeTaken(description, timer);
  return res;
}

export function getClientDebugFlags(mode?: HpClientDebugFlags): string[] {
  return getEnumFlags(mode === undefined ? v1Log.getFlags() : mode, HpClientDebugFlags as any);
}

export function toClientDebugFlags(debugWhich: string): HpClientDebugFlags {
  return toEnumFlags(debugWhich, HpClientDebugFlags as any);
}
